import { request } from '@umijs/max';

export async function getLiveCallsDashboard() {
  return request('/tenant-admin/live-calls/dashboard', {
    method: 'GET',
  });
}

export async function updateCallLogNotes(logId: number, notes: string) {
  return request(`/tenant-admin/live-calls/logs/${logId}/notes`, {
    method: 'PUT',
    data: { notes },
  });
}

/**
 * Edit the AI-generated summary of a direct call. The transcription cron only
 * writes the summary once, so a manual edit is never overwritten.
 */
export async function updateCallLogSummary(logId: number, summary: string) {
  return request(`/tenant-admin/live-calls/logs/${logId}/summary`, {
    method: 'PUT',
    data: { summary },
  });
}

export async function getUserCallLogs(userId: number, params?: Record<string, any>) {
  return request(`/tenant-admin/live-calls/user/${userId}/logs`, {
    method: 'GET',
    params: {
      ...params,
      perPage: params?.pageSize,
      page: params?.current,
    },
  }).then((res) => ({
    data: res.data?.rows || [],
    success: res.success ?? false,
    total: res.data?.pagination?.total_records || 0,
  }));
}

export async function getAllCalls(params?: Record<string, any>) {
  return request('/tenant-admin/live-calls/all', {
    method: 'GET',
    params: {
      ...params,
      perPage: params?.pageSize,
      page: params?.current,
    },
  }).then((res) => ({
    data: res.data?.rows || [],
    success: res.success ?? false,
    total: res.data?.pagination?.total_records || 0,
  }));
}

/**
 * Fetch an Appointment Center call recording as a playable blob. The audio is
 * proxied through our server (their endpoint needs the server-side admin token),
 * and fetching as a blob keeps the request on the app's normal auth path.
 */
export async function fetchApptRecording(callId: number): Promise<Blob> {
  return request(`/tenant-admin/live-calls/appt-recordings/${callId}/audio`, {
    method: 'GET',
    responseType: 'blob',
  });
}

/**
 * Fetch the AI transcript of one direct call (on demand — transcripts are too
 * large to ship with call-history list rows).
 */
export async function getCallLogTranscript(logId: number) {
  return request(`/tenant-admin/live-calls/logs/${logId}/transcript`, {
    method: 'GET',
  });
}

/**
 * Run AI transcription + summary/sentiment for one direct call immediately,
 * instead of waiting for the cron. Re-analyzing overwrites the existing
 * summary/sentiment. Can take a while for long calls.
 */
export async function analyzeCallLog(logId: number) {
  return request(`/tenant-admin/live-calls/logs/${logId}/analyze`, {
    method: 'POST',
    timeout: 300000,
  });
}

/**
 * Fetch a direct Twilio call recording as a playable blob. Twilio media URLs
 * need the account credentials, so the audio is proxied through our server.
 */
export async function fetchCallRecording(logId: number): Promise<Blob> {
  return request(`/tenant-admin/live-calls/recordings/${logId}/audio`, {
    method: 'GET',
    responseType: 'blob',
  });
}

/**
 * Update the AI summary of an Appointment Center call. Once edited, the sync
 * cron no longer overwrites it, and the dispatch's system summary note is
 * updated in place.
 */
export async function updateApptCallSummary(callId: number, summary: string) {
  return request(`/tenant-admin/live-calls/appt-calls/${callId}/summary`, {
    method: 'PUT',
    data: { summary },
  });
}

/**
 * Update the local CSR notes of an Appointment Center call (locally owned —
 * the sync never writes them). Mirrored into the dispatch's system call-notes
 * note.
 */
export async function updateApptCallNotes(callId: number, notes: string) {
  return request(`/tenant-admin/live-calls/appt-calls/${callId}/notes`, {
    method: 'PUT',
    data: { notes },
  });
}

/**
 * Details for one logical AI Agent call: headline facts, the ai/ringing/talk/hold
 * step timeline, and the correlated local CSR phone legs folded into the row.
 */
export async function getApptCallDetails(callId: number) {
  return request(`/tenant-admin/live-calls/appt-calls/${callId}/details`, {
    method: 'GET',
  });
}

export async function getCallUsers() {
  return request('/tenant-admin/live-calls/users', {
    method: 'GET',
  });
}

/** Dismissed callbacks that were never contacted (no outbound call to the number). */
export async function getAbandonedNotContacted(params?: Record<string, any>) {
  return request('/tenant-admin/live-calls/abandoned-uncontacted', {
    method: 'GET',
    params: {
      ...params,
      perPage: params?.pageSize,
      page: params?.current,
    },
  }).then((res) => ({
    data: res.data?.rows || [],
    success: res.success ?? false,
    total: res.data?.pagination?.total_records || 0,
  }));
}
